import React, { useMemo, useState } from 'react';
import Button from '../button';
import parseStoryblokLink from '../../utils/parse-storyblok-link';
import Chevron from '../../images/svg/chevron-icon.svg';
import Container from '../container';
import styled, { css } from 'styled-components';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import useEmblaCarousel from 'embla-carousel-react';
import CaseStudiesSliderItem from './CaseStudiesSliderItem';
import RichtextParser from '../../utils/richtext-parser';
import { styledLink } from '../../utils/styles';
import mq from '../../utils/mq';

const CaseStudiesSliderOuterContainer = styled.div`
  width: 100%;
  height: 100%;
  background: var(--Background-bg-inverse, #111);
  padding: 96px 0;
  &.light-theme {
    background: #fff;
  }
  ${mq.mobile(css`
    padding: 64px 0;
  `)};
`;

const TitleContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 44px;
  flex-wrap: wrap;
  padding-bottom: 38px;
  ${mq.mobile(css`
    padding-bottom: 40px;
    gap: 20px;
  `)};
`;

const TitleContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 536px;
  ${mq.mobile(css`
    gap: 12px;
  `)};
`;

const Title = styled.h2`
  color: #fff;
  font-size: 42px;
  font-style: normal;
  font-weight: 700;
  line-height: 115%;
  &.light-theme {
    color: #161616;
  }
  ${mq.mobile(css`
    font-size: 36px;
  `)};
`;

const Description = styled.div`
  color: #fff;
  font-size: 18px;
  font-weight: 400;
  line-height: 160%;
  text-wrap: pretty;
  p,
  span {
    color: #fff !important;
  }
  a {
    ${styledLink};
    text-decoration: none;
  }
  &.light-theme {
    color: #666;
    p,
    span {
      color: #666 !important;
    }
  }
`;

const CtaContainer = styled.div`
  width: fit-content;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const NavigationControlButton = styled.button<{ $disabled?: boolean }>`
  width: 48px;
  height: 48px;
  background: #645757;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  border: none;
  transition: all 0.15s ease-out;
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    border: 1px solid rgba(255, 255, 255, 1);
    backdrop-filter: blur(10px);
    color: #000;
    transition: all 0.15s ease-out;
  }

  &:active {
    transform: scale(0.97);
  }

  &.light-theme {
    background: #f8f8f8;
    color: #161616;
    &:hover {
      background-color: rgba(0, 0, 0, 1);
      border: 1px solid rgba(0, 0, 0, 1);
      color: #f8f8f8;
      transition: all 0.15s ease-out;
    }
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
      transition: all 0.3s ease-in-out;
    `}
  svg {
    width: 12px;
  }
`;

const CaseStudiesSliderContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: visible;
  overscroll-behavior-x: contain;
`;

const EmblaContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  overflow-x: visible;
  gap: 40px;
  overscroll-behavior-x: contain;
`;

const CaseStudiesSliderNavigationContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  margin-top: 32px;
`;

const SliderNavigationTopContainer = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-top: -12px;
`;

export default function CaseStudiesSlider({
  sectionTitle,
  sectionDescription,
  sectionCtaLabel,
  sectionCtaLink,
  caseStudies,
  lightTheme,
  topAlignedNavigation,
  largerContainer = false
}: {
  sectionTitle: string;
  sectionDescription: string;
  sectionCtaLabel: string;
  sectionCtaLink: string;
  caseStudies: any[];
  lightTheme?: boolean;
  topAlignedNavigation?: boolean;
  largerContainer?: boolean;
}) {
  const [canScrollNext, setcanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      skipSnaps: true,
      dragThreshold: 0.2
    },
    [
      WheelGesturesPlugin({
        forceWheelAxis: 'x'
      })
    ]
  );

  const previousSlide = () => {
    if (emblaApi) {
      if (emblaApi.canScrollPrev()) {
        emblaApi.scrollPrev();
      }
    }
  };

  const nextSlide = () => {
    if (emblaApi) {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext();
      }
    }
  };

  emblaApi?.on('scroll', () => {
    setcanScrollNext(emblaApi?.canScrollNext() || false);
    setCanScrollPrev(emblaApi?.canScrollPrev() || false);
  });

  emblaApi?.on('init', () => {
    setcanScrollNext(emblaApi?.canScrollNext() || false);
    setCanScrollPrev(emblaApi?.canScrollPrev() || false);
  });

  return (
    <CaseStudiesSliderOuterContainer
      className={lightTheme ? 'light-theme' : ''}
    >
      <Container large={largerContainer}>
        <TitleContainer>
          <TitleContent>
            <Title className={lightTheme ? 'light-theme' : ''}>
              {sectionTitle}
            </Title>
            <Description
              className={lightTheme ? 'light-theme' : ''}
              dangerouslySetInnerHTML={{
                __html: RichtextParser(sectionDescription)
              }}
            />
          </TitleContent>
          {!topAlignedNavigation && sectionCtaLabel && (
            <CtaContainer>
              <Button
                to={parseStoryblokLink(sectionCtaLink)}
                borderColorPrimary={true}
              >
                {sectionCtaLabel}
              </Button>
            </CtaContainer>
          )}
          {topAlignedNavigation && (
            <SliderNavigationTopContainer>
              <SliderNavigation
                previousSlide={previousSlide}
                canScrollPrev={canScrollPrev}
                lightTheme={lightTheme}
                nextSlide={nextSlide}
                canScrollNext={canScrollNext}
              />
            </SliderNavigationTopContainer>
          )}
        </TitleContainer>
      </Container>
      <Container style={{ overflowX: 'visible' }} large={largerContainer}>
        <CaseStudiesSliderContainer ref={emblaRef as any} className="embla">
          <EmblaContainer className="embla__container">
            {caseStudies.map((caseStudy, index) => (
              <CaseStudiesSliderItem
                key={index}
                {...caseStudy}
                lightTheme={lightTheme}
              />
            ))}
          </EmblaContainer>
        </CaseStudiesSliderContainer>
      </Container>
      {!topAlignedNavigation && (
        <Container large={largerContainer}>
          <SliderNavigation
            previousSlide={previousSlide}
            canScrollPrev={canScrollPrev}
            lightTheme={lightTheme}
            nextSlide={nextSlide}
            canScrollNext={canScrollNext}
          />
        </Container>
      )}
    </CaseStudiesSliderOuterContainer>
  );
}

const SliderNavigation = ({
  previousSlide,
  canScrollPrev,
  lightTheme,
  nextSlide,
  canScrollNext
}: {
  previousSlide: any;
  canScrollPrev: boolean;
  lightTheme?: boolean;
  nextSlide: any;
  canScrollNext: boolean;
}) => {
  return (
    <CaseStudiesSliderNavigationContainer>
      <NavigationControlButton
        onClick={previousSlide}
        $disabled={!canScrollPrev}
        style={{ paddingRight: '2px' }}
        className={lightTheme ? 'light-theme' : ''}
      >
        <Chevron />
      </NavigationControlButton>
      <NavigationControlButton
        onClick={nextSlide}
        $disabled={!canScrollNext}
        style={{ paddingLeft: '2px' }}
        className={lightTheme ? 'light-theme' : ''}
      >
        <Chevron style={{ transform: 'rotate(180deg)' }} />
      </NavigationControlButton>
    </CaseStudiesSliderNavigationContainer>
  );
};
